import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const Background_Shorthand = () => {
  return (
    <div className="tutorial-container">
      {/* Page Title */}
      <h1 className="tutorial-title">🎨 CSS Background Shorthand</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        Instead of writing multiple background properties separately, CSS lets
        you use the <code>background</code> shorthand property. It is a compact
        way to apply <strong>color, image, position, size, repeat,</strong> and{" "}
        <strong>attachment</strong> all in one line.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`/* Full shorthand syntax */
background: color image position / size repeat attachment;

/* Example */
background: #f0f0f0 url("image.jpg") no-repeat right top / cover fixed;`}
        />
      </div>

      {/* Without Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Without Shorthand</h2>
        <p>This is how we normally write background properties separately:</p>
        <CodeCopyBlock
          code={`div {
  background-color: #f0f0f0;
  background-image: url("image.jpg");
  background-repeat: no-repeat;
  background-position: right top;
  background-size: cover;
  background-attachment: fixed;
}`}
        />
      </div>

      {/* With Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">With Shorthand</h2>
        <p>
          Instead of writing multiple lines, we can combine everything into one
          shorthand line:
        </p>
        <CodeCopyBlock
          code={`div {
  background: #f0f0f0 url("image.jpg") no-repeat right top / cover fixed;
}`}
        />

        {/* Demo Box */}
        <div
          style={{
            background:
              "#f0f0f0 url('https://images.unsplash.com/photo-1507525428034-b723cf961d3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80') no-repeat right top / cover fixed",
            height: "250px",
            border: "1px solid #ccc",
            borderRadius: "12px",
            marginTop: "1rem",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: "1.2rem",
            fontWeight: "bold",
            textShadow: "1px 1px 5px rgba(0,0,0,0.7)",
          }}
        >
          ✅ This box uses background shorthand
        </div>
      </div>

      {/* Common Examples */}
      <div className="tutorial-section">
        <h2 className="section-title">Common Examples</h2>

        {/* Example 1 */}
        <h3 className="section-subtitle">1. Background Color Only</h3>
        <CodeCopyBlock code={`div { background: lightblue; }`} />
        <div
          style={{
            background: "lightblue",
            height: "120px",
            borderRadius: "8px",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 2 */}
        <h3 className="section-subtitle">2. Background Image with Repeat</h3>
        <CodeCopyBlock
          code={`div { background: url("pattern.png") repeat center; }`}
        />
        <div
          style={{
            background:
              "url('https://www.transparenttextures.com/patterns/cubes.png') repeat center",
            height: "150px",
            border: "1px solid #ccc",
            borderRadius: "8px",
            marginTop: "0.5rem",
          }}
        />

        {/* Example 3 */}
        <h3 className="section-subtitle">3. Fixed Background with Overlay</h3>
        <CodeCopyBlock
          code={`div {
  background: rgba(0,0,0,0.4) url("image.jpg") no-repeat center / cover fixed;
}`}
        />
        <div
          style={{
            background:
              "rgba(0,0,0,0.4) url('https://images.unsplash.com/photo-1526772662000-3f88f10405ff?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80') no-repeat center / cover fixed",
            height: "200px",
            borderRadius: "12px",
            marginTop: "0.5rem",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: "bold",
            fontSize: "1.1rem",
            textShadow: "1px 1px 4px rgba(0,0,0,0.6)",
          }}
        >
          🌄 Fixed Background with Overlay
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          Now that you know shorthand, let’s explore{" "}
          <a href="/css/background-position" className="learn-more">
            CSS Background Position
          </a>{" "}
          for more control over image placement.
        </p>
      </div>
    </div>
  );
};

export default Background_Shorthand;
